import TemplateSurveyState from 'containers/SurveyPage/TemplateSurveyState';
import { find, get, first, last } from 'lodash';
import { mapQuestionsAndGroupsAsNodes } from 'containers/QuestionnairePage/utils';
import { FAILURE, REASON_SCREENOUT } from 'components/LogicOutcome/constants';
import { getEmptyAnswer } from 'components/ScreeningQuestion/utils';
import { SCREENING_ALLOWED_VALUES } from 'components/ScreeningQuestion/constants';
import { Matcher } from 'utils/adl-services';
import { END_OF_SURVEY } from 'components/LogicPathPreview/constants';
import {
  SURVEY_TYPE_DISTRIBUTION_LINK,
  SURVEY_TYPE_PEEQ,
} from 'containers/Survey/constants';

import {
  screeningQuestionToNormal,
  addScreeningQuestionWithScreeningQL,
  repointCarryForwardFromScreening,
  shouldUseScreeningQl,
  shouldConvertScreeningToQlQuestions,
  applyScreeningQuestionContentUpdate,
  finalizeConvertedScreeningPlacement,
} from '../utils';

const brandAwarenessGenerator = ({
  template,
  variables,
  surveyType,
  peeqCategoryType,
}) => {
  // pass the fetchedXXXX options only
  // if you intend to use the relevant template as-is

  // extract and prepare user variables
  const productCategory = get(
    find(variables, ['name', 'product_category']),
    'value',
    {},
  );
  const yourBrand = get(find(variables, ['name', 'your_brand']), 'value', {});
  const competitorBrands = get(
    find(variables, ['name', 'competitor_brands']),
    'value',
    [],
  );
  const defaultRecurrence = get(
    find(variables, ['name', 'defaultRecurrence']),
    'value',
    undefined,
  );

  const { nodes: brandAwarenessTemplateQuestions, screeningQuestions } =
    mapQuestionsAndGroupsAsNodes(template.questionnaireTemplate);

  const Survey = new TemplateSurveyState({
    fetchedQuestionnaire: {
      ...template.questionnaireTemplate,
      isPeeq: surveyType === SURVEY_TYPE_PEEQ,
      isDistributionLink: surveyType === SURVEY_TYPE_DISTRIBUTION_LINK,
    },
    fetchedTargeting:
      surveyType === SURVEY_TYPE_PEEQ
        ? template.peeqTargetingTemplate
        : template.regularTargetingTemplate,
  });

  brandAwarenessTemplateQuestions.forEach((question) => {
    Survey.questionnaire.updateQuestion({
      ...question,
      prompt: question.prompt.replace(/\{\{your_brand\}\}/gim, yourBrand.text),
      htmlPrompt: question.htmlPrompt.replace(
        /\{\{your_brand\}\}/gim,
        yourBrand.text,
      ),
      media: yourBrand.media,
    });
  });

  // update the first screening question (replace the product_category text and media)
  const firstScreeningQuestion = screeningQuestions[0];
  const updatedFirstScreeningQuestion = {
    ...firstScreeningQuestion,
    prompt: firstScreeningQuestion.prompt.replace(
      /\{\{product_category\}\}/gim,
      productCategory.text,
    ),
    htmlPrompt: firstScreeningQuestion.htmlPrompt.replace(
      /\{\{product_category\}\}/gim,
      productCategory.text,
    ),
    media: productCategory.media,
  };

  Survey.targeting.updateScreeningQuestion(updatedFirstScreeningQuestion);
  applyScreeningQuestionContentUpdate(Survey, updatedFirstScreeningQuestion, {
    surveyType,
    peeqCategoryType,
  });

  // update the second screening question (replace your_brand answer and create the competitor ones)
  const secondScreeningQuestion = screeningQuestions[1];
  const secondScreeningQuestionAnswers = [
    // YOUR VALUE
    { ...getEmptyAnswer(), body: yourBrand.text, media: yourBrand.media },
    // COMPETITOR VALUES
    ...competitorBrands.map((brand) => ({
      ...getEmptyAnswer(),
      body: brand.text,
      media: brand.media,
      screenerType: SCREENING_ALLOWED_VALUES.NOT_ALLOWED,
    })),
  ];
  const updatedSecondScreeningQuestion = {
    ...secondScreeningQuestion,
    answers: secondScreeningQuestionAnswers,
  };
  Survey.targeting.updateScreeningQuestion(updatedSecondScreeningQuestion);
  applyScreeningQuestionContentUpdate(Survey, updatedSecondScreeningQuestion, {
    surveyType,
    peeqCategoryType,
  });

  // if creating a distribution link or PEEQ survey
  // we need to handle the screening questions
  // and convert them to normal questions
  // with a screen out reason in the ADL

  if (shouldConvertScreeningToQlQuestions({ surveyType, peeqCategoryType })) {
    // add questions
    // remove from question:isScreening
    // and from answers: screenerType, screening,
    const firstDlinkScreener = screeningQuestionToNormal(
      updatedFirstScreeningQuestion,
    );
    Survey.questionnaire.addQuestion(firstDlinkScreener);

    const secondDlinkScreener = screeningQuestionToNormal(
      updatedSecondScreeningQuestion,
    );
    Survey.questionnaire.addQuestion(secondDlinkScreener);

    repointCarryForwardFromScreening(
      Survey,
      firstDlinkScreener.refId,
      updatedFirstScreeningQuestion.refId,
    );
    repointCarryForwardFromScreening(
      Survey,
      secondDlinkScreener.refId,
      updatedSecondScreeningQuestion.refId,
    );

    // set ADL to screenout
    const firstStatementId = Survey.questionnaire.linkAnswerToQuestion({
      question: firstDlinkScreener,
      answerRefId: last(firstDlinkScreener.answers).refId,
      targetQuestion: { refId: END_OF_SURVEY },
      context: { questionId: firstDlinkScreener.refId },
    });
    Survey.questionnaire.updateStatementOutcome({
      nodeRefId: firstDlinkScreener.refId,
      statementId: firstStatementId,
      outcome: {
        params: { id: END_OF_SURVEY },
        type: FAILURE,
        reason: REASON_SCREENOUT,
      },
    });

    const secondStatementId = Survey.questionnaire.linkAnswerToQuestion({
      question: secondDlinkScreener,
      answerRefId: first(secondDlinkScreener.answers).refId,
      targetQuestion: { refId: END_OF_SURVEY },
      context: { questionId: secondDlinkScreener.refId },
      matcher: Matcher.NotEqual,
    });
    Survey.questionnaire.updateStatementOutcome({
      nodeRefId: secondDlinkScreener.refId,
      statementId: secondStatementId,
      outcome: {
        params: { id: END_OF_SURVEY },
        type: FAILURE,
        reason: REASON_SCREENOUT,
      },
    });

    finalizeConvertedScreeningPlacement(Survey, [
      {
        originalRefId: updatedFirstScreeningQuestion.refId,
        convertedQuestion: firstDlinkScreener,
      },
      {
        originalRefId: updatedSecondScreeningQuestion.refId,
        convertedQuestion: secondDlinkScreener,
      },
    ]);
  } else if (shouldUseScreeningQl({ surveyType, peeqCategoryType })) {
    Survey.questionnaire.removeQuestion(updatedFirstScreeningQuestion);
    Survey.questionnaire.removeQuestion(updatedSecondScreeningQuestion);
    Survey.targeting.removeAllScreeningQuestion();

    // add questions
    // remove from question:isScreening
    // and from answers: screenerType, screening,
    const firstQLScreener = addScreeningQuestionWithScreeningQL(
      Survey,
      updatedFirstScreeningQuestion,
    );
    applyScreeningQuestionContentUpdate(Survey, firstQLScreener, {
      surveyType,
      peeqCategoryType,
    });

    const secondQLScreener = addScreeningQuestionWithScreeningQL(
      Survey,
      updatedSecondScreeningQuestion,
    );
    applyScreeningQuestionContentUpdate(Survey, secondQLScreener, {
      surveyType,
      peeqCategoryType,
    });

    repointCarryForwardFromScreening(
      Survey,
      firstQLScreener.refId,
      updatedFirstScreeningQuestion.refId,
    );
    repointCarryForwardFromScreening(
      Survey,
      secondQLScreener.refId,
      updatedSecondScreeningQuestion.refId,
    );
  }
  return { state: Survey.state, defaultRecurrence };
};

export default brandAwarenessGenerator;
